import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CardComp from "shared/components/card";
import { toastMessage } from "shared/components/toast";
import {
  getSchoolStatisticsService,
  getSchoolReadsStatisticsService,
  getAllSchoolListService
} from "shared/services/productManagerService";
import styles from "./style.module.scss";
import Loader from "shared/components/loader/animation";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface SchoolStatisticsProps {
  selectedSchoolId?: number;
}

const SchoolStatisticsDashboard: React.FC<SchoolStatisticsProps> = ({
  selectedSchoolId
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [statistics, setStatistics] = useState<any>(null);
  const [readsStats, setReadsStats] = useState<any>(null);
  const [selectedRole, setSelectedRole] = useState<string>("all");
  const [selectedPeriod, setSelectedPeriod] = useState<string>("weekly");
  const [filterDates, setFilterDates] = useState<any>({
    startDate: null,
    endDate: null
  });
  const [schools, setSchools] = useState<any[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<any>(
    selectedSchoolId || null
  );

  useEffect(() => {
    fetchSchools();
  }, []);

  useEffect(() => {
    if (selectedSchool) {
      fetchStatistics();
      fetchReadsStatistics();
    }
  }, [
    selectedSchool,
    selectedRole,
    selectedPeriod,
    filterDates.startDate,
    filterDates.endDate
  ]);

  const fetchSchools = () => {
    getAllSchoolListService({ search: "" })
      .then(({ data }) => {
        const schoolsList = Array.isArray(data?.data) ? data.data : [];
        setSchools(schoolsList);
        if (schoolsList.length > 0 && !selectedSchoolId) {
          setSelectedSchool(schoolsList[0].id);
        }
      })
      .catch((err) => {
        console.error("Error fetching schools:", err);
        setSchools([]);
        toastMessage(
          "error",
          err?.response?.data?.message || "Failed to fetch schools"
        );
      });
  };

  const fetchStatistics = () => {
    setLoading(true);
    getSchoolStatisticsService(
      selectedSchool,
      filterDates.startDate,
      filterDates.endDate
    )
      .then(({ data }) => {
        setStatistics(data?.data);
        setLoading(false);
      })
      .catch((err) => {
        toastMessage(
          "error",
          err?.response?.data?.message || "Failed to fetch statistics"
        );
        setLoading(false);
      });
  };

  const fetchReadsStatistics = () => {
    getSchoolReadsStatisticsService(
      selectedSchool,
      selectedRole,
      selectedPeriod,
      filterDates.startDate,
      filterDates.endDate
    )
      .then(({ data }) => {
        setReadsStats(data?.data);
      })
      .catch((err) => {
        toastMessage(
          "error",
          err?.response?.data?.message || "Failed to fetch reads statistics"
        );
      });
  };

  const onChangeDate = (dates: any) => {
    const [start, end] = dates;
    setFilterDates({
      startDate: start,
      endDate: end
    });
  };

  const cardData = [
    {
      title: "Total Teachers",
      stats: statistics?.total_teachers || 0
    },
    {
      title: "Total Students",
      stats: statistics?.total_students || 0
    },
    {
      title: "Total Books in Library",
      stats: statistics?.total_books || 0
    },
    {
      title: "Active Subscription",
      stats: statistics?.active_subscription ? "Yes" : "No"
    },
    {
      title: "Total Duration (Days)",
      stats: statistics?.plan_duration || 0
    },
    {
      title: "Remaining Duration (Days)",
      stats: statistics?.remaining_duration || 0
    }
  ];

  const readsChartData = {
    labels: readsStats?.labels || [],
    datasets: [
      {
        label: "Number of Reads",
        data: readsStats?.reads || [],
        fill: false,
        borderColor: "#9A469B",
        backgroundColor: "#9A469B",
        tension: 0.4
      }
    ]
  };

  const roleBasedReadsData = {
    labels: readsStats?.role_labels || ["Teachers", "Students"],
    datasets: [
      {
        label: "Reads by Role",
        data: readsStats?.role_reads || [0, 0],
        backgroundColor: ["#9A469B", "#3f9e92"],
        borderColor: ["#9A469B", "#3f9e92"],
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          boxWidth: 12,
          padding: 10,
          font: {
            size: 11
          }
        }
      },
      title: {
        display: false
      },
      tooltip: {
        enabled: true,
        mode: "index" as const,
        intersect: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          font: {
            size: 10
          }
        }
      },
      x: {
        ticks: {
          font: {
            size: 10
          },
          maxRotation: 45,
          minRotation: 0
        }
      }
    }
  };

  return (
    <div className={styles.container}>
      <Box className={styles.topSection}>
        <h2 className={styles.heading}>School Statistics Dashboard</h2>

        <Box className={styles.filterSection}>
          <FormControl className={styles.filterControl} size="small">
            <InputLabel>Select School</InputLabel>
            <Select
              value={selectedSchool || ""}
              onChange={(e) => setSelectedSchool(e.target.value)}
              label="Select School"
              startAdornment={
                <InputAdornment position="start">
                  <SchoolIcon sx={{ color: "#9A469B", fontSize: 20 }} />
                </InputAdornment>
              }
            >
              {Array.isArray(schools) && schools.length > 0 ? (
                schools.map((school: any) => (
                  <MenuItem key={school.id} value={school.id}>
                    {school.school_name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No schools available
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <div className={styles.rangeInputWrapper}>
            <Box className={styles.datePickerBox}>
              <CalendarTodayIcon className={styles.calendarIcon} />
              <ReactDatePicker
                className={styles.range_input}
                dateFormat="d MMM yyyy"
                selected={filterDates?.startDate}
                onChange={(date: any) => onChangeDate(date)}
                startDate={filterDates?.startDate}
                endDate={filterDates?.endDate}
                selectsRange
                placeholderText="Select Date Range"
              />
            </Box>
          </div>
        </Box>
      </Box>

      {loading ? (
        <Loader />
      ) : (
        <>
          <Grid container spacing={{ xs: 1, sm: 2 }} className={styles.cardsContainer}>
            {cardData.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <CardComp
                  stats={item.stats}
                  title={item.title}
                  isLoading={loading}
                  style={styles.card}
                />
              </Grid>
            ))}
          </Grid>

          <Box className={styles.subscriptionSection}>
            <Paper className={styles.paper}>
              <h3 className={styles.sectionTitle}>Subscription Details</h3>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Plan Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Total Duration</TableCell>
                      <TableCell>Remaining Duration</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        {statistics?.plan_name || "N/A"}
                      </TableCell>
                      <TableCell>
                        <span
                          className={
                            statistics?.subscription_status === "Active"
                              ? styles.activeStatus
                              : styles.inactiveStatus
                          }
                        >
                          {statistics?.subscription_status || "Inactive"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {statistics?.start_date || "N/A"}
                      </TableCell>
                      <TableCell>
                        {statistics?.end_date || "N/A"}
                      </TableCell>
                      <TableCell>
                        {statistics?.plan_duration
                          ? `${statistics.plan_duration} Days`
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        {statistics?.remaining_duration !== undefined
                          ? `${statistics.remaining_duration} Days`
                          : "N/A"}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>

          <Box className={styles.chartsSection}>
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid item xs={12}>
                <Paper className={styles.paper}>
                  <Box className={styles.chartHeader}>
                    <h3 className={styles.sectionTitle}>Reading Statistics</h3>
                    <Box className={styles.chartFilters}>
                      <FormControl className={styles.filterControl} size="small">
                        <InputLabel>Role</InputLabel>
                        <Select
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value)}
                          label="Role"
                        >
                          <MenuItem value="all">All</MenuItem>
                          <MenuItem value="teacher">Teachers</MenuItem>
                          <MenuItem value="student">Students</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl className={styles.filterControl} size="small">
                        <InputLabel>Period</InputLabel>
                        <Select
                          value={selectedPeriod}
                          onChange={(e) => setSelectedPeriod(e.target.value)}
                          label="Period"
                        >
                          <MenuItem value="daily">Daily</MenuItem>
                          <MenuItem value="weekly">Weekly</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                  <Box className={styles.chartContainer}>
                    <Line data={readsChartData} options={chartOptions} />
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper className={styles.paper}>
                  <h3 className={styles.sectionTitle}>Reads by Role</h3>
                  <Box className={styles.chartContainer}>
                    <Bar data={roleBasedReadsData} options={chartOptions} />
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper className={styles.paper}>
                  <h3 className={styles.sectionTitle}>
                    Top Books by Reads
                  </h3>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Book Title</TableCell>
                          <TableCell>Total Reads</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {readsStats?.top_books?.length > 0 ? (
                          readsStats.top_books.map((book: any, idx: number) => (
                            <TableRow key={idx}>
                              <TableCell>{book.title}</TableCell>
                              <TableCell>{book.reads}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={2} align="center">
                              No data available
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </div>
  );
};

export default SchoolStatisticsDashboard;
